/**
 * MusteriServisKaydiTabModal
 *
 * Geniş, sekmeli salt-okunur modal: müşterinin geçmiş bir servis kaydını
 * (Servis Kaydı Oluştur ekranındaki "İstek ve Şikayet → Teknik Servis"
 * adımında) hızlıca incelemek için kullanılır.
 *  - Sekme 1: Servis kaydı bilgileri (özet)
 *  - Sekme 2: Randevular (tıklanınca AppointmentInfoModal açılır)
 */
import React, { useEffect, useState, useCallback } from 'react';
import { sanitizeString } from '../utils/security';
import {
  fetchServiceRecordDetail,
  fetchAppointmentsByServiceRecord,
} from '../services/serviceRecordsService';
import AppointmentInfoModal from './AppointmentInfoModal';

const safe = (v) => sanitizeString(v ?? '');

const formatDate = (val) => {
  if (!val) return '—';
  const d = val instanceof Date ? val : new Date(val);
  if (Number.isNaN(d.getTime())) return '—';
  return d.toLocaleDateString('tr-TR', { year: 'numeric', month: 'short', day: '2-digit' });
};

const formatDateTime = (val) => {
  if (!val) return '—';
  const d = val instanceof Date ? val : new Date(val);
  if (Number.isNaN(d.getTime())) return '—';
  return d.toLocaleString('tr-TR', {
    year: 'numeric',
    month: 'short',
    day: '2-digit',
    hour: '2-digit',
    minute: '2-digit',
  });
};

const TABS = [
  { key: 'info', label: 'Servis Kaydı Bilgileri', icon: 'fa-circle-info' },
  { key: 'appointments', label: 'Randevular', icon: 'fa-calendar-alt' },
];

const MusteriServisKaydiTabModal = ({ servisKaydiId, onClose, overlayClassName = '' }) => {
  const [activeTab, setActiveTab] = useState('info');
  const [serviceRecord, setServiceRecord] = useState(null);
  const [appointments, setAppointments] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [openAppointmentId, setOpenAppointmentId] = useState(null);

  const loadData = useCallback(async () => {
    if (!servisKaydiId) return;
    setLoading(true);
    setError('');
    try {
      const [srRes, aptRes] = await Promise.all([
        fetchServiceRecordDetail(servisKaydiId),
        fetchAppointmentsByServiceRecord(servisKaydiId, { aktif: undefined }),
      ]);
      const sr = srRes?.veri?.servisKaydi || srRes?.veri?.serviceRecord || srRes?.veri || null;
      const list = aptRes?.veri?.randevular || aptRes?.veri || [];
      setServiceRecord(sr);
      setAppointments(Array.isArray(list) ? list : []);
    } catch (err) {
      setError(err?.message || 'Servis kaydı yüklenirken hata oluştu');
      setServiceRecord(null);
      setAppointments([]);
    } finally {
      setLoading(false);
    }
  }, [servisKaydiId]);

  useEffect(() => {
    loadData();
  }, [loadData]);

  if (!servisKaydiId) return null;

  const customer = serviceRecord?.musteri || {};
  const customerName = `${customer.adi || customer.name || ''} ${customer.soyadi || customer.surname || ''}`.trim() || '—';
  const customerPhone = customer.telefon || customer.phone || customer.gsm || '—';
  const adres = serviceRecord?.adres || {};
  const cityName = adres.city?.name || adres.city?.ad || '';
  const districtName = adres.district?.name || adres.district?.ad || '';

  const Row = ({ label, value }) => (
    <div style={{ display: 'flex', gap: '12px', padding: '10px 0', borderBottom: '1px solid #f1f5f9' }}>
      <div style={{ flex: '0 0 180px', fontSize: '13px', fontWeight: 600, color: '#475569' }}>{label}</div>
      <div style={{ flex: 1, fontSize: '14px', color: '#1f2937', wordBreak: 'break-word' }}>{value || '—'}</div>
    </div>
  );

  const renderInfoTab = () => {
    if (!serviceRecord) return <div style={{ padding: 24, color: '#64748b' }}>Bilgi yok.</div>;
    return (
      <div>
        <Row label="Servis No" value={safe(serviceRecord.numara)} />
        <Row label="Müşteri" value={safe(customerName)} />
        <Row label="Telefon" value={safe(customerPhone)} />
        <Row label="İşlem Tipi" value={safe(serviceRecord.islemTipi)} />
        <Row label="Durum" value={safe(serviceRecord.durum)} />
        <Row label="Önem Derecesi" value={safe(serviceRecord.onemDerecesi)} />
        <Row label="Oluşturma" value={formatDateTime(serviceRecord.olusturmaTarihi || serviceRecord.createdAt)} />
        <Row
          label="Adres"
          value={safe([adres.adres, [districtName, cityName].filter(Boolean).join(' / ')].filter(Boolean).join(' — '))}
        />
        {serviceRecord.istekSikayetKategorisi && (
          <Row label="İstek/Şikayet Kategorisi" value={safe(serviceRecord.istekSikayetKategorisi)} />
        )}
        {serviceRecord.istekSikayetTuru && (
          <Row label="Kayıt türü" value={safe(serviceRecord.istekSikayetTuru)} />
        )}
        {serviceRecord.istekSikayetNotu && (
          <Row label="Açıklama" value={safe(serviceRecord.istekSikayetNotu)} />
        )}
        {serviceRecord.arizaNotu && <Row label="Arıza Notu" value={safe(serviceRecord.arizaNotu)} />}
        {serviceRecord.notlar && <Row label="Notlar" value={safe(serviceRecord.notlar)} />}
      </div>
    );
  };

  const renderAppointmentsTab = () => {
    if (appointments.length === 0) {
      return (
        <div
          style={{
            padding: '40px 16px',
            textAlign: 'center',
            color: '#64748b',
            background: '#f8fafc',
            borderRadius: '8px',
          }}
        >
          <i className="fas fa-calendar-times" style={{ fontSize: 28, marginBottom: 8 }}></i>
          <div>Bu servis kaydı için randevu bulunmuyor.</div>
        </div>
      );
    }

    return (
      <div className="table-container">
        <table className="admins-table">
          <thead>
            <tr>
              <th>Randevu No</th>
              <th>Tarih</th>
              <th>Saat</th>
              <th>Teknik Servis</th>
              <th>Durum</th>
              <th style={{ width: 60 }}>Detay</th>
            </tr>
          </thead>
          <tbody>
            {appointments.map((apt) => {
              const id = apt.id || apt._id;
              const ts = apt.teknikServis || apt.teknikServisId || {};
              const tsAd =
                typeof ts === 'object'
                  ? safe(ts.ad || ts.name || '')
                  : safe(ts || '');
              return (
                <tr key={id}>
                  <td>{safe(apt.randevuNumarasi || apt.numara || apt.appointmentNo || '—')}</td>
                  <td>{formatDate(apt.randevuTarihi || apt.tarih)}</td>
                  <td>{safe(apt.randevuSaati || apt.saatAraligi || apt.randevuSaatAraligi || '—')}</td>
                  <td>{tsAd || '—'}</td>
                  <td>
                    <span
                      style={{
                        display: 'inline-block',
                        padding: '4px 10px',
                        borderRadius: '999px',
                        fontSize: '12px',
                        fontWeight: 600,
                        background: '#e0f2fe',
                        color: '#075985',
                      }}
                    >
                      {safe(apt.durum || '—')}
                    </span>
                  </td>
                  <td>
                    <button
                      type="button"
                      className="icon-btn"
                      title="Randevu detayı"
                      onClick={() => setOpenAppointmentId(id)}
                      style={{
                        background: 'transparent',
                        border: '1px solid #cbd5e1',
                        borderRadius: '6px',
                        padding: '6px 10px',
                        cursor: 'pointer',
                        color: '#2563eb',
                      }}
                    >
                      <i className="fas fa-info-circle"></i>
                    </button>
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>
    );
  };

  return (
    <>
      <div
        className={`modal-overlay${overlayClassName ? ` ${overlayClassName}` : ''}`}
        role="dialog"
        aria-modal="true"
        onClick={onClose}
      >
        <div
          className="modal-content"
          onClick={(e) => e.stopPropagation()}
          style={{
            maxWidth: '1100px',
            width: '95%',
            maxHeight: '92vh',
            display: 'flex',
            flexDirection: 'column',
          }}
        >
          <div className="modal-header" style={{ flexShrink: 0 }}>
            <h2>
              <i className="fas fa-clipboard-list" style={{ marginRight: 8, color: '#2563eb' }}></i>
              Servis Kaydı — {safe(serviceRecord?.numara || '...')}
            </h2>
            <button type="button" className="modal-close-btn" onClick={onClose} aria-label="Kapat">
              <i className="fas fa-times"></i>
            </button>
          </div>

          {/* Sekmeler */}
          <div
            style={{
              display: 'flex',
              gap: '4px',
              padding: '0 20px',
              borderBottom: '1px solid #e2e8f0',
              flexShrink: 0,
            }}
          >
            {TABS.map((tab) => {
              const active = activeTab === tab.key;
              return (
                <button
                  key={tab.key}
                  type="button"
                  onClick={() => setActiveTab(tab.key)}
                  style={{
                    padding: '12px 18px',
                    border: 'none',
                    background: 'transparent',
                    borderBottom: active ? '2px solid #2563eb' : '2px solid transparent',
                    color: active ? '#2563eb' : '#475569',
                    fontWeight: active ? 600 : 500,
                    fontSize: '14px',
                    cursor: 'pointer',
                    display: 'inline-flex',
                    alignItems: 'center',
                    gap: '8px',
                  }}
                >
                  <i className={`fas ${tab.icon}`}></i>
                  {tab.label}
                  {tab.key === 'appointments' && appointments.length > 0 && (
                    <span
                      style={{
                        display: 'inline-block',
                        padding: '1px 8px',
                        borderRadius: '999px',
                        background: active ? '#2563eb' : '#cbd5e1',
                        color: '#fff',
                        fontSize: '11px',
                        fontWeight: 600,
                      }}
                    >
                      {appointments.length}
                    </span>
                  )}
                </button>
              );
            })}
          </div>

          <div className="modal-body" style={{ flex: 1, overflowY: 'auto', padding: '20px' }}>
            {loading ? (
              <div style={{ padding: '40px', textAlign: 'center', color: '#64748b' }}>
                <i className="fas fa-spinner fa-spin" style={{ fontSize: 24, marginRight: 8 }}></i>
                Yükleniyor...
              </div>
            ) : error ? (
              <div style={{ padding: '20px', color: '#b91c1c', background: '#fee2e2', borderRadius: '8px' }}>
                <i className="fas fa-exclamation-triangle" style={{ marginRight: 8 }}></i>
                {safe(error)}
              </div>
            ) : activeTab === 'info' ? (
              renderInfoTab()
            ) : (
              renderAppointmentsTab()
            )}
          </div>

          <div className="modal-footer" style={{ flexShrink: 0 }}>
            <button type="button" className="secondary-btn" onClick={onClose}>
              Kapat
            </button>
          </div>
        </div>
      </div>

      {openAppointmentId && (
        <AppointmentInfoModal
          appointmentId={openAppointmentId}
          servisKaydiId={servisKaydiId}
          onClose={() => setOpenAppointmentId(null)}
        />
      )}
    </>
  );
};

export default MusteriServisKaydiTabModal;
